/**
 * Per-app context an app hands to `trackingService.init` so the standard
 * super properties can be registered once, centrally, instead of every
 * wrapper re-implementing them (which is how 18 copies of `app_version` and a
 * stray `version` key came to exist).
 */
export type TrackingAppContext = {
  /**
   * Stable snake_case slug of the emitting app (`handyparken`, `vas_v2`,
   * `sdk_selector`, …). Lets analysts segment any event by app without parsing
   * the event-name prefix. Registry: `libs/tracking/catalog/oaf-event-catalog.yaml`.
   */
  app: string;
  /**
   * Human-readable mini name, for a mini-app only — omitted everywhere else.
   *
   * The catalog has carried `mini_app_name` as `proposed` since the framework
   * was written, noting it would be "a hardcoded constant in each mini's
   * tracking wrapper (the mini knows its own name); the SDK's banner/mini DTOs
   * carry no name field". FE-2671 is the first to send it, so it goes through
   * this builder rather than three `register` calls — a mini added later
   * inherits the property instead of having to remember it.
   *
   * Distinct from `app` on purpose even where the two strings match today:
   * `app` identifies the webview that emitted the event and exists on every
   * event in the estate, while `mini_app_name` marks the subset that happened
   * inside a mini and is what joins them to the SDK's own mini events.
   * Registry: `registries.mini_app_name`.
   */
  miniAppName?: string;
};

/** Environment-derived inputs, injected so the builder stays pure and testable. */
export type StandardSuperPropertyInputs = {
  appVersion: string | undefined;
  deploymentTarget: string | undefined;
  platform: string;
};

/**
 * Standard super properties every product-analytics event carries, per the
 * catalog's `global_properties`: `app`, `app_version`, `deployment_target`,
 * `platform`. Undefined inputs are dropped rather than sent as `undefined`
 * (Mixpanel would store the string "undefined").
 */
export const buildStandardSuperProperties = (
  { app, miniAppName }: TrackingAppContext,
  { appVersion, deploymentTarget, platform }: StandardSuperPropertyInputs,
): Record<string, string> => {
  const props: Record<string, string> = { app, platform };

  if (miniAppName) {
    props['mini_app_name'] = miniAppName;
  }

  if (appVersion) {
    props['app_version'] = appVersion;
  }

  if (deploymentTarget) {
    props['deployment_target'] = deploymentTarget;
  }

  return props;
};

export default buildStandardSuperProperties;
